import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * A map image with markers on it — the one map renderer for every surface: the
 * Electron main window, the overlay windows and admin-web.
 *
 * Deliberately pure: no context, no sockets, no game state. Markers arrive as
 * percentages of the image (`MapMarker`, built with `worldToMapPercent`), so the
 * same data draws at any size, and the box keeps the image's aspect ratio before
 * it loads so a panel never jumps.
 *
 * The image URL is resolved by the CALLER: the Electron renderers pass it
 * through their disk-cache protocol, admin-web uses it as is. This component
 * must stay free of either app's config.
 *
 * A marker outside the image (a player off this map) is not drawn; callers list
 * such players alongside the map instead of pinning them to its edge.
 *
 * `interactive` adds pan and zoom (useMapZoom), clickable markers and regions,
 * and a `focus`: the part of the map to show, which the map animates to. Image
 * `layers` sit on the map at a rectangle of it — a zone's own, more detailed
 * artwork over the part of the planet it covers — and fade in and out.
 */
import { useEffect, useRef } from 'react';
import { useMapZoom } from './useMapZoom';
function isOnMap(marker) {
    return marker.x >= 0 && marker.x <= 100 && marker.y >= 0 && marker.y <= 100;
}
function markerStyle(marker) {
    return {
        '--x': `${marker.x}%`,
        '--y': `${marker.y}%`,
        '--rx': `${marker.rx ?? 0}%`,
        '--ry': `${marker.ry ?? 0}%`,
    };
}
function rectStyle(rect) {
    return { '--x': `${rect.x}%`, '--y': `${rect.y}%`, '--w': `${rect.w}%`, '--h': `${rect.h}%` };
}
/** A rectangle as a string, so an effect can follow it by value. */
function rectKey(rect) {
    return rect ? `${rect.x}:${rect.y}:${rect.w}:${rect.h}` : '';
}
function rectFromKey(key) {
    if (!key)
        return null;
    const [x, y, w, h] = key.split(':').map(Number);
    return { x, y, w, h };
}
export function GameMap({ imageUrl, width, height, name, markers = [], size = 'fill', className, overlay, caption, showLabels = true, interactive = false, maxZoom = 8, focus = null, layers = [], regions = [], activeRegionId = null, onRegionSelect, trails = [], selectedMarkerId = null, onMarkerSelect, }) {
    const viewportRef = useRef(null);
    const planeRef = useRef(null);
    const zoom = useMapZoom(viewportRef, planeRef, { enabled: interactive, maxZoom });
    // Follow the focus: animate to it, except on first draw.
    const focusKey = rectKey(focus);
    const drawn = useRef(false);
    const { zoomToRect, reset } = zoom;
    useEffect(() => {
        if (!interactive)
            return;
        const animate = drawn.current;
        drawn.current = true;
        const target = rectFromKey(focusKey);
        void (target ? zoomToRect(target, animate) : reset(animate));
    }, [interactive, focusKey, zoomToRect, reset]);
    const visible = markers.filter(isOnMap);
    const classes = ['game-map', `game-map--${size}`, interactive && 'game-map--interactive', className]
        .filter(Boolean).join(' ');
    const selectable = interactive && !!onMarkerSelect;
    const select = (id) => (event) => {
        event.stopPropagation();
        onMarkerSelect?.(id === selectedMarkerId ? null : id);
    };
    return (_jsxs("div", { className: classes, children: [_jsxs("div", { ref: viewportRef, className: "game-map__frame", style: { aspectRatio: `${width} / ${height}` }, onClick: selectable ? () => onMarkerSelect?.(null) : undefined, children: [_jsxs("div", { ref: planeRef, className: "game-map__plane", children: [_jsx("img", { className: "game-map__image", src: imageUrl, alt: name ?? 'Map', draggable: false }), layers.map((layer) => (_jsx("img", { className: `game-map__layer${layer.visible ? ' is-visible' : ''}`, style: rectStyle(layer.rect), src: layer.imageUrl, alt: layer.visible ? layer.name ?? '' : '', draggable: false }, layer.id))), interactive && regions.map((region) => (_jsx("button", { type: "button", className: `game-map__region${region.id === activeRegionId ? ' is-active' : ''}`, style: rectStyle(region.rect), "aria-label": region.label ?? 'Zone', onClick: (event) => {
                                    event.stopPropagation();
                                    onRegionSelect?.(region.id);
                                }, children: region.label && _jsx("span", { className: "game-map__region-label", children: region.label }) }, region.id))), trails.length > 0 && (_jsx("svg", { className: "game-map__trails", viewBox: "0 0 100 100", preserveAspectRatio: "none", "aria-hidden": "true", children: trails.map((trail) => (_jsx("polyline", { className: `game-map__trail game-map__marker--${trail.tone}${trail.faded ? ' is-faded' : ''}${trail.selected ? ' is-selected' : ''}`, points: trail.points.map((p) => `${p.x},${p.y}`).join(' '), vectorEffect: "non-scaling-stroke" }, trail.id))) })), visible.map((marker) => {
                                const modifiers = [
                                    `game-map__marker--${marker.tone}`,
                                    marker.stale && 'is-stale',
                                    marker.out && 'is-out',
                                    marker.id === selectedMarkerId && 'is-selected',
                                ].filter(Boolean).join(' ');
                                const style = markerStyle(marker);
                                const shape = marker.kind === 'circle' ? 'game-map__circle' : 'game-map__dot';
                                return (_jsxs("span", { children: [selectable ? (_jsx("button", { type: "button", className: `game-map__marker ${shape} ${modifiers}`, style: style, "aria-label": marker.label ?? 'Marker', "aria-pressed": marker.id === selectedMarkerId, onClick: select(marker.id) })) : (_jsx("span", { className: `game-map__marker ${shape} ${modifiers}`, style: style, "aria-label": marker.label })), marker.kind === 'circle' && _jsx("span", { className: `game-map__marker game-map__centre ${modifiers}`, style: style }), marker.label && showLabels && (_jsxs("span", { className: `game-map__label${marker.out ? ' is-out' : ''}`, style: style, children: [marker.label, marker.sublabel && _jsx("small", { children: marker.sublabel })] }))] }, marker.id));
                            })] }), interactive && (_jsxs("div", { className: "game-map__controls", onClick: (event) => event.stopPropagation(), children: [_jsx("button", { type: "button", className: "game-map__control", "aria-label": "Zoom in", onClick: zoom.zoomIn, children: "+" }), _jsx("button", { type: "button", className: "game-map__control", "aria-label": "Zoom out", onClick: zoom.zoomOut, children: "\u2212" }), _jsx("button", { type: "button", className: "game-map__control", "aria-label": "Fit", onClick: () => void (focus ? zoom.zoomToRect(focus) : zoom.reset()), children: "\u2922" })] })), overlay && _jsx("div", { className: "game-map__overlay", children: overlay })] }), caption && _jsx("span", { className: "game-map__caption", children: caption })] }));
}
